import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _, { isArray } from "lodash";
import Joi from "joi-browser";
import SubForm from "../../components/PartnerManage/SubForm";
import {
  getBprmForm,
  loadBprmForm,
  getStateCountyCity,
  getDataTypeCodemanager,
  saveBprmForm,
  updateBprmForm,
  getUpdateStatus,
  allStateCountyCityReceived,
  getTaskFormSavedStatus,
  taskFormSaved,
  getAllButtonProcessIds,
  formUpdated,
  updateSummaryBprmForm,
  getSummaryFormUpdateStatus,
  getSavedFormDocSasUrls,
  formSaved,
  summaryFormUpdated,
} from "../../app/store/bprmForm";
import {
  getApplication,
  loadApplication,
  getApplicationData,
} from "../../app/store/application";
import {
  workflowTaskDone,
  executeBprmFormRule,
  getExecuteRuleAppData,
  getTaskDoneStatus,
  taskDone,
} from "../store/WorkflowEngine";
import Label from "./BprmDynamicControls/Label";
import TextBox from "./BprmDynamicControls/TextBox";
import Dropdown from "./BprmDynamicControls/Dropdown";
import Date from "./BprmDynamicControls/Date";
import Button from "./BprmDynamicControls/Button";
import Multiline from "./BprmDynamicControls/Multiline";
import { getCurrentUser } from "../store/auth";
import { getAlert, getConfirmAlert } from "../../services/appAlerts";
import Radio from "./BprmDynamicControls/Radio";
import FileUpload from "./BprmDynamicControls/FileUpload";
import geoService, {
  getAllCountries,
  getGeoCountyData,
  getGeoStateData,
  getStateList,
} from "../../services/geoService";
import CheckBox from "./BprmDynamicControls/CheckBox";
import {
  getTemplatePdf,
  printTemplatePdf,
  templatePdfReceived,
} from "../store/template";
import ViewFile from "./BprmDynamicControls/ViewFile";
import DecryptAppData from "../../components/DecryptAppData";
import { CircularProgress, Button as MUIButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { loadAllButtonAlertMessages } from "../store/layoutUI";
import config from "../../config.json";
import {
  formatPhoneNumber,
  getDropdownCodemanager,
} from "../../services/appService";
import useDocDownload from "../hooks/useDocDownload";
import { getDocuments } from "../store/documents";
import { Link } from "react-router-dom";
import { uploadFileToBlob } from "../../services/blobUploadService";
import CountryCodePicker from "./BprmDynamicControls/CountryCodePicker";

// ─── Module-level style constants (no recreation per render) ──────────────────
const PHONE_LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.7px",
  marginBottom: "6px",
  display: "flex",
  alignItems: "center",
  gap: "5px",
};
const PHONE_ICON_STYLE = { color: "#94a3b8", fontSize: "11px" };
const PHONE_ERROR_STYLE = {
  marginTop: 6,
  padding: "5px 10px",
  fontSize: "11.5px",
  borderRadius: "7px",
};
const UPDATE_BTN_SX = {
  "&.Mui-disabled": { backgroundColor: "#9e9e9e", color: "#333333", opacity: 1 },
  mr: 1,
  mb: 1,
  textTransform: "none",
  border: "1px solid #172b4d",
  color: "black",
  "&:hover": { backgroundColor: "#172b4d", color: "white", borderColor: "#172b4d" },
};
const TD_CSS = {
  border: "1px solid #c1a0d9",
  fontWeight: "600",
  textTransform: "uppercase",
  fontSize: "0.875rem",
  color: "black",
};

// ─── groupPhoneFields — pure function, runs once per form via useMemo ─────────
const groupPhoneFields = (elements) => {
  if (!elements || !Array.isArray(elements)) return [];
  const grouped = [];
  for (let i = 0; i < elements.length; i++) {
    const col = elements[i];
    const hasCountryCode = col.components?.some(
      (comp) =>
        comp.dataType === "dropDown" && comp.dropdownType === "countrycode",
    );

    if (hasCountryCode && i + 1 < elements.length) {
      const nextCol = elements[i + 1];
      const hasPhone = nextCol.components?.some(
        (comp) =>
          comp.dataType === "textBox" &&
          ["phone", "mobile"].includes(comp.attributeDataTypes?.type || ""),
      );

      if (hasPhone) {
        const countryComp = col.components.find(
          (comp) =>
            comp.dataType === "dropDown" && comp.dropdownType === "countrycode",
        );
        const phoneComp = nextCol.components.find(
          (comp) =>
            comp.dataType === "textBox" &&
            ["phone", "mobile"].includes(comp.attributeDataTypes?.type || ""),
        );
        const w1 = parseInt(col.width, 10) || 0;
        const w2 = parseInt(nextCol.width, 10) || 0;
        const combinedWidth = w1 + w2 > 12 ? 12 : w1 + w2;

        grouped.push({
          ...col,
          width: combinedWidth.toString(),
          isMergedPhone: true,
          countryCodeComp: countryComp,
          phoneComp: phoneComp,
          components: [countryComp, phoneComp],
        });
        i++; // skip next col
        continue;
      }
    }
    grouped.push(col);
  }
  return grouped;
};

// ─── Build Joi schema from elements — pure, memoizable ───────────────────────
const buildSchema = (elements) => {
  const schema = {};
  if (!elements || elements.length === 0) return schema;
  for (const col of elements) {
    if (col.isMergedPhone) {
      // Phone group
      const { countryCodeComp, phoneComp } = col;
      if (phoneComp?.isRequired) {
        const id =
          phoneComp.attributeType?.toLowerCase() + "." + phoneComp.attributeObject;
        if (id !== ".") schema[id] = Joi.string().required().label(phoneComp.label || "Phone Number");
      }
      if (countryCodeComp?.isRequired) {
        const id =
          countryCodeComp.attributeType?.toLowerCase() + "." + countryCodeComp.attributeObject;
        if (id !== ".") schema[id] = Joi.string().required().label(countryCodeComp.label || "Country Code");
      }
      continue;
    }
    if (!col.components) continue;
    for (const comp of col.components) {
      if (comp.isRequired && comp.dataType !== "esign") {
        const id =
          (comp.attributeType?.toLowerCase() || "") +
          (comp.attributeObject ? "." + comp.attributeObject : "");
        if (id && id !== ".") {
          schema[id] = Joi.string()
            .required()
            .label(comp.label || "Field is required");
        }
      }
    }
  }
  return schema;
};

// ─── Derive frmDDTypes from elements — pure ───────────────────────────────────
const buildFrmDDTypes = (elements) => {
  const types = new Set();
  if (!elements) return [];
  for (const col of elements) {
    for (const comp of col.components || []) {
      if (comp.attributeType) types.add(comp.attributeType);
    }
  }
  return [...types];
};

export default function WorkflowElement(props) {
  const {
    wfTask,
    handleNextTask,
    appSummary,
    viewType,
    codeVerified,
    updatedStateData,
    wfTaskType,
    saveBtnProcessStatus,
    setSaveBtnProcessStatus,
    workflowFormData,
    documentInfo,
    handleCloseButton,
    actionType,
    wfForm,
    rateObj,
    subFormObj,
  } = props;

  const [stateSchema, setStateSchema] = useState({});
  const [stateData, setStateData] = useState({});
  const [stateErrors, setStateErrors] = useState({});
  const [encryptionDecryptions, setEncryptionDecryptions] = useState({});
  const [documents, setDocuments] = useState([]);
  const [aWorkfinityAppEntity, setAworkfinityAppEntity] = useState("");
  const [aWorkfinityApp, setAworkfinityApp] = useState("");
  const [stateCountyCity, setStateCountyCity] = useState([]);
  const [subformDataType, setSubformDataType] = useState([]);
  const [ddTypeCodMngr, setDdTypeCodMngr] = useState([]);
  const [subformItemIndex, setSubformItemIndex] = useState(1);
  const [codeIsVerified, setCodeIsVerified] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [btnProcessStatus, setBtnProcessStatus] = useState(saveBtnProcessStatus);
  const [isTaskForm, setIsTaskForm] = useState(false);
  const [currentButtonType, setCurrentButtonType] = useState("");
  const [currentButtonMessage, setCurrentButtonMessage] = useState("");
  const [printBtnProccess, setPrintBtnProccess] = useState(false);
  const [decryptShowHide, setDecryptShowHide] = useState(false);
  const [isEncryptedForm, setIsEncryptedForm] = useState(false);

  const applicationSelector = useSelector(getApplication);
  const ruleAppDataSelector = useSelector(getExecuteRuleAppData);
  const summaryFormUpdateStatusSelector = useSelector(getSummaryFormUpdateStatus);
  const taskFormSavedStatusSelector = useSelector(getTaskFormSavedStatus);
  const taskDoneStatusSelector = useSelector(getTaskDoneStatus);
  const templatePdfSelector = useSelector(getTemplatePdf);
  const documentsSelector = useSelector(getDocuments);
  const savedFormDocSasUrlsSelector = useSelector(getSavedFormDocSasUrls);

  const [handleDownloadFile] = useDocDownload();
  const dispatch = useDispatch();
  const printBody = useRef(null);

  // Track previous geo-key to skip redundant loadStateCounty calls
  const prevGeoKeyRef = useRef("");

  // ─── Memoized elements — JSON.parse runs only when wfForm changes ──────────
  const elements = useMemo(() => {
    if (!wfForm?.elementJson) return [];
    try {
      return groupPhoneFields(JSON.parse(wfForm.elementJson));
    } catch {
      return [];
    }
  }, [wfForm]);

  // ─── Memoized schema — built once per form layout ─────────────────────────
  const derivedSchema = useMemo(() => buildSchema(elements), [elements]);

  // ─── Sync derived schema into stateSchema (which Joi validation reads) ─────
  useEffect(() => {
    setStateSchema(derivedSchema);
  }, [derivedSchema]);

  // ─── Memoized frmDDTypes — no more in-render push() ──────────────────────
  const frmDDTypes = useMemo(() => buildFrmDDTypes(elements), [elements]);

  // ─── compNum for template layout ─────────────────────────────────────────
  const compNum = useMemo(() => {
    if (elements.length === 0 || wfTaskType !== "template") return 0;
    return elements.reduce(
      (max, col) => Math.max(max, col.components?.length ?? 0),
      0,
    );
  }, [elements, wfTaskType]);

  // =========================================================================
  // Effects
  // =========================================================================

  useEffect(() => {
    if (!saveBtnProcessStatus) {
      setBtnProcessStatus(false);
      if (setSaveBtnProcessStatus) setSaveBtnProcessStatus(null);
    }
  }, [saveBtnProcessStatus]);

  useEffect(() => {
    if (wfTask?.ciid > 0 && !appSummary) dispatch(loadApplication(wfTask?.ciid));
    setIsDisabled(!!viewType);
  }, [wfTask]);

  useEffect(() => {
    if (codeVerified) setCodeIsVerified(true);
  }, [codeVerified]);

  useEffect(() => {
    if (applicationSelector.length > 0) {
      const latestApp = applicationSelector[applicationSelector.length - 1];
      const obj = getApplicationData(latestApp.applicationData);
      if (obj?.aWorkfinityApp) setAworkfinityApp(obj.aWorkfinityApp);
      if (obj?.aWorkfinityAppEntity) setAworkfinityAppEntity(obj.aWorkfinityAppEntity);

      if (workflowFormData && obj?.data) {
        const newData = { ...obj.data, ...workflowFormData };
        if (rateObj?.candidateRate)
          newData["candidateonboardingform.Rate_hour_Paid_to_Candidate"] =
            rateObj.candidateRate.toString();
        setStateData(newData);
      } else if (obj?.data) {
        if (rateObj?.candidateRate)
          obj.data["candidateonboardingform.Rate_hour_Paid_to_Candidate"] =
            rateObj.candidateRate.toString();
        setStateData(obj.data);
      }
    } else {
      setAworkfinityApp("");
      setAworkfinityAppEntity("");
      setStateData({});
    }
  }, [applicationSelector]);

  // Re-run geo loading when stateData changes, but only if relevant fields changed
  useEffect(() => {
    if (!wfForm) return;
    // Build a key from geo-relevant fields to skip redundant fetches
    const geoKey = JSON.stringify(
      Object.keys(stateData)
        .filter((k) => k.includes("country") || k.includes("state") || k.includes("county") || k.includes("city"))
        .sort()
        .map((k) => k + "=" + stateData[k]),
    );
    if (geoKey === prevGeoKeyRef.current) return;
    prevGeoKeyRef.current = geoKey;
    loadStateCounty();
  }, [stateData]);

  // Local parent→child dropdown filtering — no API call
  useEffect(() => {
    if (ddTypeCodMngr.length === 0) return;
    const updResult = ddTypeCodMngr.map((dd) => ({ ...dd }));
    let changed = false;
    updResult.forEach((dd) => {
      if (!dd.childControlId) return;
      const childIdx = updResult.findIndex((x) => x.controlId === dd.childControlId);
      if (childIdx === -1) return;
      const pData = stateData[dd.controlId];
      if (pData) {
        const pObj = dd.allOptionData?.find((x) => x.name === pData);
        const newOptions = pObj
          ? updResult[childIdx].allOptionData?.filter((x) => x.parentCode === pObj.code) ?? []
          : [];
        const prev = updResult[childIdx].optionData;
        if (!prev || prev.length !== newOptions.length) {
          updResult[childIdx] = { ...updResult[childIdx], optionData: newOptions };
          changed = true;
        }
      } else if (updResult[childIdx].optionData?.length > 0) {
        updResult[childIdx] = { ...updResult[childIdx], optionData: [] };
        changed = true;
      }
    });
    if (changed) setDdTypeCodMngr(updResult);
  }, [stateData]);

  // Form-level initialisation — runs once per wfForm change
  useEffect(() => {
    if (!wfForm || wfForm === "") return;

    // Determine disabled state
    let disabled = false;
    if (wfForm?.isEdit !== null && !wfForm?.isEdit && wfTaskType !== "viewForm") {
      disabled = false;
    }
    if (wfTask?.taskDoneStatus === 4) disabled = true;
    if (
      wfTask?.wfPhase === "Approved" &&
      wfTask?.formId === config.formIds.candidate.ReportingForm &&
      wfTask?.taskDoneStatus === 4
    ) {
      disabled = false;
    }
    setIsDisabled(disabled);
    setIsEncryptedForm(wfForm.isEncrypted);
    setBtnProcessStatus(false);
    setDocuments([]);
    setDdTypeCodMngr([]);
    prevGeoKeyRef.current = ""; // reset geo cache for new form
    loadButtonProcessIds();
    loadDDType();
  }, [wfForm]);

  useEffect(() => {
    if (updatedStateData && updatedStateData !== "") {
      const obj = getApplicationData(updatedStateData);
      if (obj?.aWorkfinityApp) setAworkfinityApp(obj.aWorkfinityApp);
      if (obj?.aWorkfinityAppEntity) setAworkfinityAppEntity(obj.aWorkfinityAppEntity);
      if (obj?.data) setStateData(obj.data);
    } else {
      setStateData({});
    }
  }, [updatedStateData]);

  useEffect(() => {
    if (subFormObj) {
      setStateData((prev) => ({ ...prev, ...subFormObj }));
    }
  }, [subFormObj]);

  useEffect(() => {
    if (summaryFormUpdateStatusSelector) {
      getAlert("", "Updated successfully", "success");
      dispatch(formUpdated(false));
      dispatch(summaryFormUpdated(false));
    }
  }, [summaryFormUpdateStatusSelector]);

  useEffect(() => {
    if (taskFormSavedStatusSelector && isTaskForm) {
      dispatch(workflowTaskDone(wfTask));
      dispatch(taskFormSaved(false));
      setIsTaskForm(false);
    } else if (taskFormSavedStatusSelector) {
      setIsTaskForm(false);
      dispatch(taskFormSaved(false));
      setBtnProcessStatus(false);
    }
    if (currentButtonMessage !== "") {
      getAlert("", currentButtonMessage, "success");
      setCurrentButtonMessage("");
      setCurrentButtonType("");
    }
  }, [taskFormSavedStatusSelector]);

  useEffect(() => {
    if (taskDoneStatusSelector) {
      dispatch(taskDone({ status: false }));
      if (currentButtonMessage !== "") {
        getAlert("", currentButtonMessage, "success");
        setCurrentButtonMessage("");
        setCurrentButtonType("");
      }
      handleNextTask(currentButtonType);
    }
  }, [taskDoneStatusSelector]);

  useEffect(() => {
    const uploadAllDocuments = async () => {
      if (savedFormDocSasUrlsSelector?.length > 0) {
        try {
          await Promise.all(
            documents.map((file, key) =>
              uploadFileToBlob(
                file.file,
                savedFormDocSasUrlsSelector[key]?.uploadUrl,
              ),
            ),
          );
          dispatch(formSaved({ sasResp: null }));
        } catch (error) {
          // Upload failed silently — could add a toast here
        }
      }
    };
    uploadAllDocuments();
  }, [savedFormDocSasUrlsSelector]);

  useEffect(() => {
    if (ruleAppDataSelector !== "") {
      const appShortName = Object.keys(ruleAppDataSelector)[1];
      const newAWorkfinityAppEntity = ruleAppDataSelector[appShortName];
      if (newAWorkfinityAppEntity !== "") {
        setStateData((prev) => {
          const data = { ...prev };
          Object.keys(newAWorkfinityAppEntity).forEach((keyValue) => {
            if (
              keyValue !== "@xmlns:xsd" &&
              keyValue !== "@xmlns:xsi" &&
              keyValue !== "EntityId"
            ) {
              Object.keys(newAWorkfinityAppEntity[keyValue]).forEach((keyValue1) => {
                if (keyValue1 !== "EntityId") {
                  const item = newAWorkfinityAppEntity[keyValue][keyValue1];
                  data[keyValue + "." + keyValue1.replace("@", "")] =
                    typeof item === "object" && !Array.isArray(item) ? [item] : item;
                }
              });
            }
          });
          return data;
        });
      }
    }
  }, [ruleAppDataSelector]);

  useEffect(() => {
    if (templatePdfSelector) {
      setPrintBtnProccess(false);
      dispatch(templatePdfReceived(null));
      const url = window.URL.createObjectURL(templatePdfSelector);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
  }, [templatePdfSelector]);

  // =========================================================================
  // API loaders
  // =========================================================================

  const loadDDType = async () => {
    const ddResult = getDataTypeCodemanager(wfForm);
    if (ddResult.ddTypeCodMngr.length > 0) {
      const result = await Promise.all(
        ddResult.ddTypeCodMngr.map(async (code) => {
          const ddItems = await getDropdownCodemanager(code.dataType, code.attribute);
          let childId = "";
          if (code.childDataType) childId = code.childDataType.toLowerCase();
          if (code.childAttribute) childId += "." + code.childAttribute;
          return {
            controlId: code.dataType.toLowerCase() + "." + code.attribute,
            optionData: ddItems,
            allOptionData: ddItems,
            childControlId: childId,
          };
        }),
      );
      setDdTypeCodMngr(result);
    }
    if (ddResult.subformDataType.length > 0) {
      setSubformDataType(ddResult.subformDataType);
    }
  };

  const loadStateCounty = async () => {
    const addResult = await getStateCountyCity(wfForm, stateData);
    const presult = await Promise.all(
      Object.keys(addResult).map(async (x) => {
        const obj = addResult[x];
        if (obj) {
          const nObj = await Promise.all(
            obj.stateObjects.map(async (s) => {
              const rs = await geoService.getGeoData(s.id);
              return { ...s, list: rs };
            }),
          );
          obj.stateObjects = nObj;

          const cObj = await Promise.all(
            obj.countyObjects.map(async (s) => {
              const stList = nObj.filter((x) => x.childCompId === s.compId);
              if (stList.length > 0) {
                const stObj = stList[0].list.filter((x) => x.name === s.name);
                if (stObj.length > 0) {
                  const rs = await geoService.getGeoData(stObj[0].geonameId);
                  return { ...s, list: rs, id: stObj[0].geonameId };
                }
              }
            }),
          );
          obj.countyObjects = cObj;

          const ccObj = await Promise.all(
            obj.cityObjects.map(async (s) => {
              const ctList = cObj.filter((x) => x?.childCompId === s.compId);
              if (ctList.length > 0) {
                const stObj = ctList[0]?.list?.filter((x) => x.name === s.name);
                if (stObj?.length > 0) {
                  const rs = await geoService.getGeoData(stObj[0].geonameId);
                  return { ...s, list: rs, id: stObj[0].geonameId };
                }
              }
            }),
          );
          obj.cityObjects = ccObj;
        }
        return obj;
      }),
    );
    dispatch(allStateCountyCityReceived(presult));
  };

  const loadButtonProcessIds = async () => {
    const addResult = await getAllButtonProcessIds(wfForm);
    if (addResult.length) dispatch(loadAllButtonAlertMessages(addResult));
  };

  // =========================================================================
  // Event handlers — all wrapped in useCallback for stable references
  // =========================================================================

  const validateProperty = useCallback(
    ({ name, value }) => {
      if (stateSchema[name] == null) return null;
      const { error } = Joi.validate({ [name]: value }, { [name]: stateSchema[name] });
      return error ? error.details[0].message : null;
    },
    [stateSchema],
  );

  const validate = useCallback(() => {
    if (wfTask?.status) return true;
    const result = Joi.validate(stateData, stateSchema, { abortEarly: false, allowUnknown: true });
    if (!result.error) return null;
    const errors = {};
    for (const item of result.error.details) errors[item.path[0]] = item.message;
    return errors;
  }, [stateData, stateSchema, wfTask]);

  const handleChange = useCallback(
    (input) => {
      setStateData((prevData) => {
        const data = { ...prevData, [input.name]: input.value };

        const finalRate = parseFloat(data["candidateonboardingform.Final_Rate_hour_Paid_to_Candidate"]);
        const techFee = parseFloat(data["candidateonboardingform.Candidate_Less_TechFee"]);

        if (!isNaN(finalRate)) {
          if (!isNaN(techFee)) {
            if (techFee <= finalRate) {
              data["candidateonboardingform.Rate_hour_Paid_to_Candidate"] =
                parseFloat(finalRate - techFee).toFixed(2);
            } else {
              data["candidateonboardingform.Rate_hour_Paid_to_Candidate"] = "";
            }
          } else {
            data["candidateonboardingform.Rate_hour_Paid_to_Candidate"] = finalRate;
          }
        }
        return data;
      });

      setStateErrors((prevErrors) => {
        const errorMessage = validateProperty(input);
        if (errorMessage) {
          if (prevErrors[input.name] === errorMessage) return prevErrors;
          return { ...prevErrors, [input.name]: errorMessage };
        }
        if (!prevErrors[input.name]) return prevErrors;
        const next = { ...prevErrors };
        delete next[input.name];
        return next;
      });

      // Separate rate error
      const techFee = parseFloat(input.value);
      const finalRate = parseFloat(stateData["candidateonboardingform.Final_Rate_hour_Paid_to_Candidate"]);
      if (input.name === "candidateonboardingform.Candidate_Less_TechFee" && !isNaN(techFee) && !isNaN(finalRate) && techFee > finalRate) {
        setStateErrors((prev) => ({ ...prev, "candidateonboardingform.Candidate_Less_TechFee": "Tech Fee cannot be greater than Final Rate" }));
      }
    },
    [validateProperty, stateData],
  );

  const addEncryptionFieldData = useCallback(
    (name, value) => {
      const newValue = value.replace(/\-/g, "");
      const modifiedValue = "*".repeat(newValue.length);
      setEncryptionDecryptions((prev) => {
        if (prev[name + "encrypt"]) {
          return { ...prev, [name]: prev[name] + value.replace(/[^\d]/g, "") };
        }
        return { ...prev, [name]: modifiedValue };
      });
      setStateData((prev) => {
        if (encryptionDecryptions[name + "encrypt"]) {
          return { ...prev, [name]: modifiedValue };
        }
        return { ...prev, [name]: value.replace(/[^\d]/g, "") };
      });
    },
    [encryptionDecryptions],
  );

  const handeOnChange = useCallback(
    ({ target: input }) => {
      handleChange(input);
      if (input.dataset?.encryption) addEncryptionFieldData(input.name, input.value);
    },
    [handleChange, addEncryptionFieldData],
  );

  const handleEncryptionDecryption = useCallback(
    (name) => {
      setEncryptionDecryptions((prev) => {
        const encryp = { ...prev };
        if (encryp[name + "encrypt"]) {
          const actual = encryp[name];
          const encrypted = stateData[name];
          encryp[name] = encrypted;
          encryp[name + "encrypt"] = false;
          setStateData((d) => ({ ...d, [name]: actual }));
        } else {
          const encrypted = encryp[name];
          const actual = stateData[name];
          encryp[name] = actual;
          encryp[name + "encrypt"] = true;
          setStateData((d) => ({ ...d, [name]: encrypted }));
        }
        return encryp;
      });
    },
    [stateData],
  );

  const onFileChange = useCallback(
    (event) => {
      setDocuments((prevDocs) => {
        const docs = [...prevDocs];
        const docIndex = docs.findIndex((x) => x.refId === event.target.name);
        if (docIndex > -1) docs.splice(docIndex, 1);
        let indx = docs.length > 0 ? docs[docs.length - 1].id : 0;
        for (const file of event.target.files) {
          indx += 1;
          const ext = file.name.split(".");
          docs.push({
            id: indx,
            name: file.name,
            newName: file.name,
            type: file.type,
            file,
            refId: event.target.name,
            extension: ext.length > 1 ? ext[1] : "",
            size: file.size,
          });
        }
        validateDocuments(docs);
        return docs;
      });
      setStateData((prev) => ({ ...prev, [event.target.id]: event.target.files[0].name }));
    },
    [],
  );

  const validateDocuments = useCallback(
    (docs) => {
      if (docs.length > 0) {
        setStateData((prev) => ({ ...prev, docs: docs.length.toString() }));
        setStateErrors((prev) => {
          if (!prev.docs) return prev;
          const next = { ...prev };
          delete next.docs;
          return next;
        });
      } else {
        const obj = { schedule: "" };
        const schema = { schedule: stateSchema["docs"] };
        const { error } = Joi.validate(obj, schema);
        setStateErrors((prev) => ({ ...prev, docs: error?.details[0].message ?? "" }));
        setStateData((prev) => ({ ...prev, docs: "" }));
      }
    },
    [stateSchema],
  );

  // Build app entity payload — extracted from doSubmit for clarity
  const buildAppEntityPayload = useCallback(() => {
    if (!aWorkfinityAppEntity || !aWorkfinityApp) return null;
    const entity = { ...aWorkfinityAppEntity };
    Object.keys(stateData).forEach((keyValue) => {
      const dd = keyValue.split(".");
      if (dd.length > 1 && dd[1] !== "undefined" && entity[dd[0].toLowerCase()] !== undefined) {
        const item = stateData[keyValue];
        if (Array.isArray(item)) entity[dd[0].toLowerCase()][dd[1]] = item;
        else entity[dd[0].toLowerCase()]["@" + dd[1]] = item;
      }
    });
    const app = { ...aWorkfinityApp };
    const appShortName = Object.keys(app)[1];
    app[appShortName] = entity;
    return app;
  }, [stateData, aWorkfinityAppEntity, aWorkfinityApp]);

  const doSubmit = useCallback(
    (btn, alMsg) => {
      const btnType = btn.name;
      setCurrentButtonMessage(alMsg);
      setBtnProcessStatus(true);
      setCurrentButtonType(btnType);

      if (wfTaskType === "form" || wfTaskType === "subform") {
        handleNextTask(stateData, btnType);
        return;
      }

      const app = buildAppEntityPayload();
      if (!app) return;

      const frmData = new FormData();
      frmData.append("appEntity", JSON.stringify(app));
      frmData.append("workItem", JSON.stringify(wfTask));
      frmData.append("upDocs", JSON.stringify(documents));
      frmData.append(
        "files",
        JSON.stringify(
          documents.map(({ id, name, newName, type, extension, refId, size }) => ({
            id, name, newName, type, extension, refId, size,
          })),
        ),
      );

      if (documentInfo) {
        frmData.append("docType", documentInfo.docType);
        frmData.append("updType", documentInfo.updType);
        frmData.append("message", documentInfo.message);
      }

      dispatch(saveBprmForm(frmData));

      if (wfTaskType === "viewForm") {
        handleNextTask(frmData);
      } else if (btnType !== "save" && wfTask?.wiid > 0) {
        setIsTaskForm(true);
      }
    },
    [wfTaskType, stateData, wfTask, documents, documentInfo, buildAppEntityPayload, handleNextTask],
  );

  const updateBprmFormData = useCallback(async () => {
    const app = buildAppEntityPayload();
    if (!app) return;
    const disValus = _.uniq(frmDDTypes);
    const currentUser = getCurrentUser();
    const frmData = new FormData();
    frmData.append("appEntity", JSON.stringify(app));
    frmData.append("ciid", wfTask.ciid);
    frmData.append("ddTypes", disValus);
    frmData.append("formId", wfTask.formId);
    frmData.append("performerId", currentUser.id);
    frmData.append("userName", currentUser.name);
    dispatch(updateSummaryBprmForm(frmData));
  }, [buildAppEntityPayload, frmDDTypes, wfTask]);

  const getEvaluatedValue = useCallback(
    async (comp) => {
      const app = buildAppEntityPayload();
      if (!app) return;
      const formData = new FormData();
      formData.append("appEntity", JSON.stringify(app));
      formData.append("appId", 1);
      formData.append("ruleId", comp.rule);
      dispatch(executeBprmFormRule(formData));
    },
    [buildAppEntityPayload],
  );

  const handleClose = useCallback(() => {
    if (handleCloseButton) handleCloseButton();
  }, [handleCloseButton]);

  const handleDecryptShowHide = useCallback(() => {
    setDecryptShowHide((prev) => !prev);
  }, []);

  const verifySecurityCode = useCallback((code) => {
    if (code !== "12345") {
      getAlert("Invalid Code", "", "error");
    } else {
      getAlert("Authentication Successful", "", "success");
      setCodeIsVerified(true);
      setDecryptShowHide(false);
    }
  }, []);

  const handleSubformModalShowHide = useCallback((name) => {
    const modelId = name.split("-");
    if (modelId.length > 1) {
      setStateData((prev) => ({ ...prev, [modelId[0]]: !prev[modelId[0]] }));
      setSubformItemIndex(parseInt(modelId[1]));
    } else {
      setStateData((prev) => ({ ...prev, [name]: !prev[name] }));
      setSubformItemIndex(-1);
    }
  }, []);

  const handleSubformSave = useCallback(
    (subFormObj, subFormDd, subFormAtt) => {
      const subformControlId = subFormDd.toLowerCase() + "." + subFormAtt;
      setStateData((prev) => {
        const existing = prev[subformControlId];
        let subFormArray;
        if (existing?.length > 0) {
          if (subformItemIndex > -1) {
            subFormArray = [...existing];
            subFormArray[subformItemIndex] = subFormObj;
          } else {
            subFormArray = [...existing, subFormObj];
          }
        } else {
          subFormArray = [subFormObj];
        }
        return { ...prev, [subformControlId]: subFormArray };
      });
      setAworkfinityAppEntity((prev) => {
        if (!prev) return prev;
        const entity = { ...prev };
        const existing = entity[subFormDd.toLowerCase()]?.[subFormAtt];
        let subFormArray;
        if (existing?.length > 0) {
          if (subformItemIndex > -1) {
            subFormArray = Array.isArray(existing) ? [...existing] : [existing];
            subFormArray[subformItemIndex] = subFormObj;
          } else {
            subFormArray = [...(Array.isArray(existing) ? existing : [existing]), subFormObj];
          }
        } else {
          subFormArray = [subFormObj];
        }
        entity[subFormDd.toLowerCase()] = { ...entity[subFormDd.toLowerCase()], [subFormAtt]: subFormArray };
        return entity;
      });
    },
    [subformItemIndex],
  );

  const handleEditSubForm = useCallback(() => {}, []);

  const handlePrintPdf = useCallback(async () => {
    setPrintBtnProccess(true);
    const logImg = (() => {
      const c = document.createElement("canvas");
      const base64 = c.toDataURL();
      const img = document.createElement("img");
      img.height = "80";
      img.width = "80";
      img.src = base64;
      return img;
    })();
    const frmData = new FormData();
    let outHtml = printBody.current.outerHTML;
    outHtml = outHtml.replace(/<br\s*\/?>/g, "");
    outHtml = outHtml.replaceAll('<span hidden="">​#imgClose1</span>', "</img>");
    outHtml = outHtml.replaceAll('<span hidden="">​#imgClose</span>', logImg.outerHTML + "</img>");
    frmData.append("html", outHtml);
    dispatch(printTemplatePdf(frmData));
  }, []);

  // =========================================================================
  // Render helpers
  // =========================================================================

  const renderMergedPhoneField = useCallback(
    (countryComp, phoneComp) => {
      const countryControleId =
        countryComp.attributeType?.toLowerCase() + "." + countryComp.attributeObject;
      const phoneControleId =
        phoneComp.attributeType?.toLowerCase() + "." + phoneComp.attributeObject;
      const isFieldRequired = phoneComp.isRequired || countryComp.isRequired;

      return (
        <div className={`form-group ${isFieldRequired ? "required" : ""}`} style={{ marginBottom: "16px" }}>
          <label className="form-control-label" htmlFor={phoneControleId} style={PHONE_LABEL_STYLE}>
            <i className="fa fa-phone" style={PHONE_ICON_STYLE} />
            {phoneComp.label || "Phone No."}
          </label>

          <div className="wf-merged-phone-wrapper">
            <div className="wf-phone-country-select">
              <CountryCodePicker
                name={countryControleId}
                id={countryControleId}
                value={stateData[countryControleId] || ""}
                disabled={isDisabled}
                onChange={(evt) =>
                  handeOnChange({ target: { name: evt.target.name, value: evt.target.value, type: "dropdown" } })
                }
              />
            </div>
            <div className="wf-phone-divider" />
            <div className="wf-phone-input-container">
              <input
                type="text"
                id={phoneControleId}
                name={phoneControleId}
                className="form-control wf-phone-input"
                value={formatPhoneNumber(stateData[phoneControleId] || "")}
                onChange={(event) => {
                  const raw = event.target.value.replace(/[^\d]/g, "");
                  if (raw.length <= 10) handleChange({ name: phoneControleId, value: raw, type: "text" });
                }}
                disabled={isDisabled}
                placeholder={phoneComp.label || "Phone No."}
              />
            </div>
          </div>

          {stateErrors[countryControleId] && (
            <div className="alert alert-danger" style={PHONE_ERROR_STYLE}>
              {stateErrors[countryControleId]}
            </div>
          )}
          {stateErrors[phoneControleId] && (
            <div className="alert alert-danger" style={PHONE_ERROR_STYLE}>
              {stateErrors[phoneControleId]}
            </div>
          )}
        </div>
      );
    },
    [stateData, stateErrors, isDisabled, handeOnChange, handleChange],
  );

  const getSubformTableHeader = useCallback(
    (comp) => {
      if (comp.itemColumns?.length === 0)
        return (
          <tr>
            <th className="p-2" style={TD_CSS}>Datatype not found</th>
          </tr>
        );
      return (
        <tr>
          {comp.itemColumns.filter((x) => x.visible === false).map((item) => (
            <th key={item.name} className="p-2" style={TD_CSS}>{item.name}</th>
          ))}
          {comp.isEdit && <th className="p-2" style={TD_CSS}>Action</th>}
        </tr>
      );
    },
    [],
  );

  const getSubformTableBody = useCallback(
    (comp) => {
      if (Object.keys(aWorkfinityAppEntity).length === 0) return null;
      if (comp.itemColumns?.length === 0) return null;
      const dataObj = aWorkfinityAppEntity[comp.attributeType?.toLowerCase()]?.[comp.attributeObject];
      if (!dataObj) return null;
      const dtObjArray = isArray(dataObj) ? dataObj : [dataObj];
      if (dtObjArray.length > 0) {
        return dtObjArray.map((obj, key) => (
          <tr key={key}>
            {comp.itemColumns.filter((x) => x.visible === false).map((item) => (
              <td key={item.type} className="p-2">
                {item.ddType === "link" && CreateTableCellLink(item, obj, key)}
                {item.ddType !== "link" && obj["@" + item.type]}
              </td>
            ))}
            {comp.isEdit && (
              <td className="p-2">
                <button className="btn btn-sm btn-outline-dark">Edit</button>
              </td>
            )}
          </tr>
        ));
      }
      return <tr><td>No Data</td></tr>;
    },
    [aWorkfinityAppEntity],
  );

  const CreateTableCellLink = useCallback(
    (item, obj, key) => {
      let cellData = obj["@" + item.type];
      const cellSource = item.source.split("_");
      const linkUrl = cellSource[0] ?? "";
      const linkDdType = cellSource[1] ?? "";
      const linkAtt = cellSource[2] ?? "";
      if (!cellData) cellData = item.name;
      let queryData =
        linkDdType === item.ObjectAttributeType
          ? obj["@" + linkAtt]
          : linkDdType !== "" && linkAtt !== ""
          ? stateData[linkDdType.toLocaleLowerCase() + "." + linkAtt]
          : cellData;
      const data = { id: key, name: item.name, appId: wfTask.ciid, data: queryData };
      return (
        <Link to={`/${linkUrl}?linkData=${JSON.stringify(data)}`} target="_blank">
          {cellData}
        </Link>
      );
    },
    [stateData, wfTask],
  );

  const getDataWithTable = useCallback(
    (col) => {
      const allCompts = col?.components.filter((x) => !isArray(x));
      if (allCompts.length === 0) return null;
      const rowsCompts = allCompts.splice(allCompts.length / 2);
      const colCompts = allCompts.splice(allCompts.length - rowsCompts.length);
      return (
        <table className="table table-bordered table-sm">
          <thead className="table-light">
            <tr>
              {colCompts.map((cItem, i) => (
                <th key={i} style={{ whiteSpace: "pre-wrap", letterSpacing: "0px", textTransform: "none" }}>
                  {cItem.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {rowsCompts.map((rItem, i) => (
                <td key={i}>
                  {rItem?.dataType === "link" ? (
                    <a
                      target="_blank"
                      href={`${rItem.url}?linkData=${stateData[rItem?.attributeType?.toLowerCase() + "." + rItem.attributeObject]}`}
                      rel="noreferrer"
                    >
                      {rItem.label}
                    </a>
                  ) : (
                    stateData[rItem?.attributeType?.toLowerCase() + "." + rItem.attributeObject]
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      );
    },
    [stateData],
  );

  const getSubformDataBoxLayout = useCallback(
    (subformElement) => {
      if (Object.keys(aWorkfinityAppEntity).length === 0) return null;
      const findAttObj = subformElement.filter(
        (x) => x.attributeObject !== undefined && x.attributeObject !== "",
      );
      let dtObjArray = [];
      if (findAttObj.length > 0) {
        const dataObj =
          aWorkfinityAppEntity[findAttObj[0].attributeType?.toLowerCase()]?.[findAttObj[0].attributeObject];
        if (dataObj && !isArray(dataObj)) dtObjArray = [dataObj];
        else if (dataObj) dtObjArray = dataObj;
      }

      const getColWidth = (w) => {
        switch (w) {
          case 1: return "";
          case 2: return "10%";
          case 3: return "15%";
          case 4: return "20%";
          default: return "";
        }
      };

      const withDataComp = subformElement.filter((x) => x.attributeObject !== undefined && x.attributeObject !== "");
      const withOutDataComp = subformElement.filter((x) => x.attributeObject === undefined || x.attributeObject === "");

      const hanleSubformData = (input, key, ctId) => {
        const { currentTarget: { value } } = input;
        const subformControlId =
          findAttObj[0].attributeType.toLowerCase() + "." + findAttObj[0].attributeObject;
        setStateData((prev) => {
          const existing = prev[subformControlId];
          let subFormArray;
          if (existing?.length > 0 && key > -1) {
            subFormArray = [...existing];
            subFormArray[key] = { ...subFormArray[key], ["@" + ctId]: value };
          } else {
            subFormArray = existing ? [...existing] : [];
          }
          return { ...prev, [subformControlId]: subFormArray };
        });
        setAworkfinityAppEntity((prev) => {
          const entity = { ...prev };
          const existing = entity[findAttObj[0].attributeType.toLowerCase()]?.[findAttObj[0].attributeObject];
          let subFormArray = existing ? (isArray(existing) ? [...existing] : [existing]) : [];
          if (key > -1 && subFormArray[key]) {
            subFormArray[key] = { ...subFormArray[key], ["@" + ctId]: value };
          }
          entity[findAttObj[0].attributeType.toLowerCase()] = {
            ...entity[findAttObj[0].attributeType.toLowerCase()],
            [findAttObj[0].attributeObject]: subFormArray,
          };
          return entity;
        });
      };

      const getLinkDocuments = (updId) => {
        const docs = documentsSelector.filter((x) => x.uploadId === updId);
        if (docs.length > 0) {
          return (
            <>
              <br />
              {docs.map((doc) => (
                <button key={doc.docName} className="btn btn-sm btn-link p-0" onClick={() => handleDownloadFile(doc)}>
                  {doc.docName}
                </button>
              ))}
            </>
          );
        }
        return <></>;
      };

      return (
        <div className="col-md-12">
          {withOutDataComp
            .filter((x) => !x?.colName)
            .map((col, ci) => (
              <div key={ci}>
                {col.components.map((c, cj) => {
                  if (c.dataType === "label" && !c?.colName) {
                    return <Label key={cj} comp={c} controleId="" stateData={stateData} />;
                  }
                  return null;
                })}
              </div>
            ))}
          <table className="table table-bordered table-sm">
            <thead className="table-light">
              <tr>
                {(withDataComp.length > 0 ? withDataComp : withOutDataComp)
                  .filter((x) => x?.colName)
                  .map((col, i) => (
                    <th key={i} width={getColWidth(parseInt(col.width))}>{col.colName}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {dtObjArray?.map((obj, key) => (
                <tr key={key}>
                  {withDataComp.map((col, ci) => {
                    const groupNames = _.uniq(
                      col.components.filter((x) => x.groupName !== undefined).map((c) => c.groupName),
                    );
                    return (
                      <td key={ci}>
                        {groupNames.map((g) => {
                          const grpComps = col.components.filter((x) => x.groupName === g);
                          return (
                            <div key={g}>
                              {grpComps.map((c, idx) => {
                                const controleId =
                                  (c.attributeType?.toLowerCase() || "") +
                                  (c.attributeObject ? "." + c.attributeObject : "");
                                if (c.dataType === "label") {
                                  if (c.attributeDataTypes?.type === "phone") {
                                    return <span key={idx}>{formatPhoneNumber(obj["@" + c.attributeObject])}</span>;
                                  }
                                  return (
                                    <span key={idx} className={`mr-1 ${c.attributeDataTypes?.type}`}>
                                      {c.attributeObject ? obj["@" + c.attributeObject] : ""}
                                    </span>
                                  );
                                }
                                if (c.dataType === "multiLineBox") {
                                  return (
                                    <textarea
                                      key={idx}
                                      name={controleId}
                                      id={controleId}
                                      value={obj["@" + c.attributeObject]}
                                      className="form-control"
                                      onChange={(input) => hanleSubformData(input, key, c.attributeObject)}
                                    />
                                  );
                                }
                                if (c.dataType === "dropDown") {
                                  return (
                                    <select
                                      key={idx}
                                      className="form-control"
                                      id={controleId}
                                      name={controleId}
                                      value={obj["@" + c.attributeObject]}
                                      onChange={(input) => hanleSubformData(input, key, c.attributeObject)}
                                    >
                                      <option value="">Select</option>
                                      {c?.dropdownvalue?.map((dd) => (
                                        <option key={dd.value} value={dd.value}>{dd.name}</option>
                                      ))}
                                    </select>
                                  );
                                }
                                if (c.dataType === "link" && c.attributeDataTypes?.type === "ddrms") {
                                  return <React.Fragment key={idx}>{getLinkDocuments(parseInt(obj["@" + c.attributeObject]))}</React.Fragment>;
                                }
                                return null;
                              })}
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    [aWorkfinityAppEntity, stateData, documentsSelector, handleDownloadFile],
  );

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <fieldset disabled={false}>
      <style>{`
        .form-group.required .form-control-label::after {
          content: " *" !important;
          position: static !important;
          font-family: inherit !important;
          color: #ef4444 !important;
          font-weight: 800 !important;
          font-size: 13px !important;
          line-height: 1 !important;
          margin-left: 3px !important;
        }
        .form-group.required .form-control-label::before {
          content: "" !important;
          display: none !important;
        }
      `}</style>

      {wfTaskType === "template" && (
        <>
          <div id="printBody" ref={printBody}>
            <table className="table table-bordered bg-white">
              {elements.map((col, ri) => (
                <tr key={ri}>
                  {col.components
                    .filter((x) => x.dataType !== "button")
                    .map((comp, ci) => {
                      const controleId =
                        (comp.attributeType?.toLowerCase() || "") +
                        (comp.attributeObject ? "." + comp.attributeObject : "");
                      return (
                        <td
                          key={ci}
                          style={{ whiteSpace: "pre-wrap" }}
                          colSpan={col.components.length === 1 ? compNum : compNum - col.components.length}
                        >
                          {comp.dataType === "label" && <Label comp={comp} controleId={controleId} stateData={stateData} />}
                          {comp.dataType === "textBox" && (
                            <TextBox
                              comp={comp}
                              controleId={controleId}
                              stateData={stateData}
                              stateErrors={stateErrors}
                              stateSchema={stateSchema}
                              codeIsVerified={codeIsVerified}
                              isDisabled={isDisabled}
                              handleChange={handleChange}
                              handeOnChange={handeOnChange}
                              getEvaluatedValue={getEvaluatedValue}
                            />
                          )}
                          {comp.dataType === "multiLineBox" && (
                            <Multiline
                              comp={comp}
                              controleId={controleId}
                              stateData={stateData}
                              stateErrors={stateErrors}
                              isDisabled={isDisabled}
                              encryptionDecryptions={encryptionDecryptions}
                              handeOnChange={handeOnChange}
                              getEvaluatedValue={getEvaluatedValue}
                              handleEncryptionDecryption={handleEncryptionDecryption}
                            />
                          )}
                        </td>
                      );
                    })}
                </tr>
              ))}
            </table>
          </div>
        </>
      )}

      {wfTaskType !== "template" && (
        <div className="row">
          {!codeVerified && isEncryptedForm && (
            <div className="col-md-12 form-group">
              <button
                className="btn btn-outline-default btn-sm btn-min-width"
                onClick={handleDecryptShowHide}
              >
                Decrypt
              </button>
            </div>
          )}

          {getSubformDataBoxLayout(elements.filter((x) => x.groupName === "SubFormTable"))}

          {elements
            .filter((x) => x.groupName !== "SubFormTable")
            .map((col, colIdx) => {
              let showColData = true;
              if (col.isVisible) {
                showColData = false;
                if (col?.visibleAttributeType && col?.visibleAttributeObject) {
                  const vDd = col.visibleAttributeType.toLowerCase() + "." + col.visibleAttributeObject;
                  const matches = col.visibleRuleValue
                    ?.split(",")
                    .map((v) => v.trim().toLowerCase())
                    .includes(stateData[vDd]?.trim().toLowerCase());
                  if (matches) showColData = true;
                }
              }

              if (!showColData) return null;

              return (
                <div
                  key={colIdx}
                  className={`col-${col.width} ${col?.cssclass ?? ""}`}
                >
                  {col?.groupName === "table" && getDataWithTable(col)}

                  {col?.groupName !== "table" && col?.groupName !== "SubFormTable" && col.isMergedPhone &&
                    renderMergedPhoneField(col.countryCodeComp, col.phoneComp)}

                  {col?.groupName !== "table" && col?.groupName !== "SubFormTable" && !col.isMergedPhone &&
                    col.components.map((comp, key) => {
                      const controleId =
                        (comp.attributeType?.toLowerCase() || "") +
                        (comp.attributeObject ? "." + comp.attributeObject : "");

                      if (comp.dataType === "list") {
                        const formDD = subformDataType?.filter(
                          (x) => x.dataType === comp.attributeType && x.attribute === comp.attributeObject,
                        );
                        return (
                          <React.Fragment key={key}>
                            <SubForm
                              showHide={stateData["showHide" + comp.subform]}
                              handleModalShowHide={handleSubformModalShowHide}
                              title={comp.label}
                              subFormId={comp.subform}
                              aWorkfinityAppEntity={aWorkfinityAppEntity}
                              subFormDataType={comp.attributeType}
                              subFormAttribute={comp.attributeObject}
                              handleSubformSave={handleSubformSave}
                              subformItemIndex={subformItemIndex}
                              formDD={formDD}
                              handleEditSubForm={handleEditSubForm}
                              comp={comp}
                            />
                            <table className="border w-100 mt-2" id={`table-to-xls-${comp.subform}`}>
                              <thead style={{ backgroundColor: "#d7e0f2", color: "black" }}>
                                {getSubformTableHeader(comp)}
                              </thead>
                              <tbody>{getSubformTableBody(comp)}</tbody>
                            </table>
                          </React.Fragment>
                        );
                      }

                      if (comp.dataType === "button") {
                        if (comp.buttonType?.type === "editForm" || comp.buttonType?.type === "subform") {
                          return (
                            <Button
                              key={key}
                              comp={comp}
                              btnProcessStatus={btnProcessStatus}
                              isDisabled={isDisabled}
                              isEdit={wfForm?.isEdit}
                              handleClose={handleClose}
                              handleSubformModalShowHide={handleSubformModalShowHide}
                            />
                          );
                        }
                        return null;
                      }

                      if (comp.dataType === "label") {
                        return <Label key={key} comp={comp} controleId={controleId} stateData={stateData} />;
                      }
                      if (comp.dataType === "textBox") {
                        return (
                          <TextBox
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            stateSchema={stateSchema}
                            codeIsVerified={codeIsVerified}
                            isDisabled={isDisabled}
                            handleChange={handleChange}
                            handeOnChange={handeOnChange}
                            getEvaluatedValue={getEvaluatedValue}
                          />
                        );
                      }
                      if (comp.dataType === "dropDown") {
                        return (
                          <Dropdown
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            codeIsVerified={codeIsVerified}
                            isDisabled={isDisabled}
                            stateCountyCity={stateCountyCity}
                            setStateCountyCity={setStateCountyCity}
                            ddTypeCodMngr={ddTypeCodMngr}
                            setDdTypeCodMngr={setDdTypeCodMngr}
                            handleChange={handleChange}
                            handeOnChange={handeOnChange}
                            setIsCountryStateChanged={setIsDisabled}
                          />
                        );
                      }
                      if (comp.dataType === "date") {
                        return (
                          <Date
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            codeIsVerified={codeIsVerified}
                            isDisabled={isDisabled}
                            handleChange={handleChange}
                          />
                        );
                      }
                      if (comp.dataType === "multiLineBox") {
                        return (
                          <Multiline
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            isDisabled={isDisabled}
                            encryptionDecryptions={encryptionDecryptions}
                            handeOnChange={handeOnChange}
                            getEvaluatedValue={getEvaluatedValue}
                            handleEncryptionDecryption={handleEncryptionDecryption}
                          />
                        );
                      }
                      if (comp.dataType === "radio") {
                        return (
                          <Radio
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            isDisabled={isDisabled}
                            handeOnChange={handeOnChange}
                          />
                        );
                      }
                      if (comp.dataType === "checkBox") {
                        return (
                          <CheckBox
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            isDisabled={isDisabled}
                            handleChange={handleChange}
                            ddTypeCodMngr={ddTypeCodMngr}
                          />
                        );
                      }
                      if (comp.dataType === "file") {
                        return (
                          <FileUpload
                            key={key}
                            comp={comp}
                            controleId={controleId}
                            stateData={stateData}
                            stateErrors={stateErrors}
                            isDisabled={isDisabled}
                            onFileChange={onFileChange}
                          />
                        );
                      }
                      if (comp.dataType === "viewFile") {
                        return <ViewFile key={key} controlId={controleId} comp={comp} />;
                      }
                      return null;
                    })}
                </div>
              );
            })}

          <div className="col-md-12">
            <div className="row">
              {(appSummary ||
                (wfTask?.wfPhase === "Approved" &&
                  wfTask?.formId === config.formIds.candidate.ReportingForm &&
                  wfTask?.taskDoneStatus === 4)) && (
                <div className="col-md-12 text-center mb-2">
                  <MUIButton
                    variant="outline"
                    color="inherit"
                    size="small"
                    onClick={updateBprmFormData}
                    disabled={viewType}
                    startIcon={btnProcessStatus ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                    sx={UPDATE_BTN_SX}
                  >
                    Update
                  </MUIButton>
                </div>
              )}

              {!appSummary && (
                <div className="col-md-12 text-center mb-2">
                  {elements.map((col, ci) =>
                    col.components
                      .filter((x) => x.dataType === "button")
                      .map((comp, bi) => {
                        if (
                          comp.buttonType?.type !== "taskCompleted" &&
                          comp.buttonType?.type !== "submit" &&
                          comp.buttonType?.type !== "subform" &&
                          comp.buttonType?.type !== "saveAndContinue"
                        ) {
                          return (
                            <Button
                              key={`${ci}-${bi}`}
                              comp={comp}
                              btnProcessStatus={btnProcessStatus}
                              isDisabled={isDisabled}
                              setIsDisabled={setIsDisabled}
                              validate={validate}
                              doSubmit={doSubmit}
                              isEdit={wfForm?.isEdit}
                              handleClose={handleClose}
                              currentButtonType={currentButtonType}
                            />
                          );
                        }
                        return null;
                      }),
                  )}
                </div>
              )}
            </div>

            <div className="card shadow-none border">
              <div className="d-flex flex-row justify-content-center card-header text-center bg-lighter p-2">
                {actionType === "workflow" && (
                  <h4 className="mb-0 text-dark">
                    <i className="fa fa-arrow-right mr-2" aria-hidden="true" />
                    WORKFLOW ACTION &nbsp;&nbsp;{" "}
                  </h4>
                )}

                {!appSummary && (
                  <>
                    {wfTask?.wfPhase === "Approved" &&
                    wfTask?.formId === config.formIds.candidate.ReportingForm &&
                    wfTask?.taskDoneStatus === 4
                      ? ""
                      : elements.map((col, ci) =>
                          col.components
                            .filter((x) => x.dataType === "button")
                            .map((comp, bi) => {
                              if (
                                comp.buttonType?.type === "taskCompleted" ||
                                comp.buttonType?.type === "submit" ||
                                comp.buttonType?.type === "saveAndContinue"
                              ) {
                                return (
                                  <Button
                                    key={`${ci}-${bi}`}
                                    comp={comp}
                                    btnProcessStatus={btnProcessStatus}
                                    isDisabled={isDisabled}
                                    setIsDisabled={setIsDisabled}
                                    validate={validate}
                                    doSubmit={doSubmit}
                                    isEdit={wfForm?.isEdit}
                                    handleClose={handleClose}
                                    size="btn-sm"
                                    currentButtonType={currentButtonType}
                                  />
                                );
                              }
                              return null;
                            }),
                        )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {wfTaskType === "template" && (
        <div className="col-md-12 text-center">
          <button className="btn btn-outline-dark" disabled={printBtnProccess} onClick={handlePrintPdf}>
            Print
          </button>
        </div>
      )}

      <DecryptAppData
        decryptShowHide={decryptShowHide}
        handleDecryptShowHide={handleDecryptShowHide}
        verifySecurityCode={verifySecurityCode}
      />
    </fieldset>
  );
}
